import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import SaleBanner from "@/components/SaleBanner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import SearchPage from "@/pages/SearchPage";
import HistoryPage from "@/pages/HistoryPage";
import SitesManagementPage from "@/pages/SitesManagementPage";
import UserManagementPage from "@/pages/UserManagementPage";
import TCCSearchPage from "@/pages/TCCSearchPage";
import CategoriesManagementPage from "@/pages/CategoriesManagementPage";
import ActivationCodesPage from "@/pages/ActivationCodesPage";
import AbuseAlertsPage from "@/pages/AbuseAlertsPage";
import NDAPage from "@/pages/NDAPage";
import LegalPage from "@/pages/LegalPage";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Public routes on which the "For sale" banner is displayed.
// LandingPage already includes its own SaleBanner (positioned inside its layout),
// so we exclude "/" to avoid duplication.
const PUBLIC_BANNER_ROUTES = ["/login", "/change-password", "/legal"];

const PublicBannerWrapper = () => {
  const location = useLocation();
  const isPublic = PUBLIC_BANNER_ROUTES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  if (!isPublic) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[60]">
      <SaleBanner />
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize default sites on first load
    const initSites = async () => {
      try {
        await axios.post(`${API}/init-default-sites`);
      } catch (error) {
        console.log("Sites init:", error.response?.data?.message || "Error");
      }
    };
    initSites();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PublicBannerWrapper />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/:section" element={<LegalPage />} />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <SearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites"
              element={
                <ProtectedRoute adminOnly={true}>
                  <SitesManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute adminOnly={true}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute adminOnly={true}>
                  <CategoriesManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activation-codes"
              element={
                <ProtectedRoute adminOnly={true}>
                  <ActivationCodesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/abuse-alerts"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AbuseAlertsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/nda"
              element={
                <ProtectedRoute adminOnly={true}>
                  <NDAPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tcc"
              element={
                <ProtectedRoute>
                  <TCCSearchPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;